'use client'
import * as React from 'react'
import { CheckCircle2, Loader2, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { TurnstileWidget, useTurnstileSiteKey } from '@/lib/turnstile'

// Newsletter signup. Submissions land in /api/leads tagged with
// intent: 'other', source: '<source>', toolSlug: 'newsletter'.
// Honeypot field "company_url" drops bot submissions.
export function NewsletterForm({
  source = 'newsletter',
  variant = 'pill',
  eyebrow = 'Newsletter',
  title = 'Get the next post first.',
  body = 'Monthly UGC + shoppable-video benchmarks, A/B post-mortems and migration teardowns. No spam, unsubscribe in one click.',
}: {
  source?: string
  variant?: 'pill' | 'card' | 'compact'
  eyebrow?: string
  title?: string
  body?: string
}) {
  const [state, setState] = React.useState<'idle' | 'submitting' | 'ok' | 'error'>('idle')
  const [error, setError] = React.useState('')
  const [turnstileToken, setTurnstileToken] = React.useState('')
  const turnstileSiteKey = useTurnstileSiteKey()

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setState('submitting')
    setError('')
    const fd = new FormData(e.currentTarget)
    const payload = {
      email:    String(fd.get('email') || ''),
      intent:   'other',
      source,
      toolSlug: 'newsletter',
      message:  'Newsletter subscription',
      company_url: String(fd.get('company_url') || ''),
      'cf-turnstile-response': turnstileToken,
    }
    try {
      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || !json?.ok) { setState('error'); setError(json?.error || 'Something went wrong.'); return }
      setState('ok')
    } catch {
      setState('error')
      setError('Network error. Try again or email hi@idukki.io.')
    }
  }

  if (state === 'ok') {
    return (
      <div className={cn(
        'flex items-center gap-3 rounded-xl border bg-card p-4 text-sm',
        variant === 'card' && 'p-6',
      )}>
        <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
        <div>
          <p className="font-medium">Subscribed.</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Welcome, your first post lands within a week.</p>
        </div>
      </div>
    )
  }

  // Pill variant, horizontal email + submit, ideal for footer.
  if (variant === 'pill') {
    return (
      <form onSubmit={onSubmit} className="w-full">
        <div className="hidden">
          <label>Company website
            <input type="text" name="company_url" tabIndex={-1} autoComplete="off" />
          </label>
        </div>
        <div className="flex items-stretch gap-2 rounded-full border bg-card p-1.5 shadow-sm focus-within:border-primary/40">
          <Input
            type="email"
            name="email"
            required
            placeholder="you@brand.com"
            className="h-9 flex-1 border-0 bg-transparent px-3 text-sm shadow-none focus-visible:ring-0"
            autoComplete="email"
          />
          <Button
            type="submit"
            size="sm"
            disabled={state === 'submitting' || (Boolean(turnstileSiteKey) && !turnstileToken)}
            className="rounded-full px-4"
          >
            {state === 'submitting' ? (<><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> …</>) : 'Subscribe'}
          </Button>
        </div>
        {turnstileSiteKey && (
          <div className="mt-2"><TurnstileWidget size="flexible" onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} /></div>
        )}
        {state === 'error' && error && <p role="alert" className="mt-2 text-xs text-destructive">{error}</p>}
        <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">No spam · unsubscribe in one click</p>
      </form>
    )
  }

  if (variant === 'compact') {
    return (
      <form onSubmit={onSubmit} className="w-full">
        <div className="hidden"><input type="text" name="company_url" tabIndex={-1} autoComplete="off" /></div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input type="email" name="email" required placeholder="you@brand.com" autoComplete="email" className="flex-1" />
          <Button type="submit" disabled={state === 'submitting' || (Boolean(turnstileSiteKey) && !turnstileToken)}>
            {state === 'submitting' ? (<><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Subscribing…</>) : (<><Mail className="mr-1.5 h-4 w-4" /> Subscribe</>)}
          </Button>
        </div>
        {turnstileSiteKey && (
          <div className="mt-2"><TurnstileWidget size="flexible" onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} /></div>
        )}
        {state === 'error' && error && <p role="alert" className="mt-2 text-xs text-destructive">{error}</p>}
        <p className="mt-2 text-[10px] uppercase tracking-widest text-muted-foreground">No spam · unsubscribe in one click</p>
      </form>
    )
  }

  // Card variant, full-section block.
  return (
    <div className="rounded-2xl border bg-card p-6 sm:p-8">
      <div className="grid gap-6 md:grid-cols-[1fr_minmax(0,440px)] md:items-center md:gap-10">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary">{eyebrow}</p>
          <h3 className="mt-2 font-display text-2xl font-medium tracking-tight md:text-3xl text-balance">{title}</h3>
          <p className="mt-3 text-sm text-muted-foreground md:text-base">{body}</p>
        </div>
        <form onSubmit={onSubmit}>
          <div className="hidden"><input type="text" name="company_url" tabIndex={-1} autoComplete="off" /></div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input type="email" name="email" required placeholder="you@brand.com" autoComplete="email" className="flex-1" />
            <Button type="submit" disabled={state === 'submitting' || (Boolean(turnstileSiteKey) && !turnstileToken)} className="sm:w-auto">
              {state === 'submitting' ? (<><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Subscribing…</>) : (<><Mail className="mr-1.5 h-4 w-4" /> Subscribe</>)}
            </Button>
          </div>
          {turnstileSiteKey && (
            <div className="mt-3 w-full">
              <TurnstileWidget size="flexible" onToken={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
            </div>
          )}
          {state === 'error' && error && <p role="alert" className="mt-2 text-xs text-destructive">{error}</p>}
          <p className="mt-2 text-[11px] uppercase tracking-widest text-muted-foreground">No spam · unsubscribe in one click · GDPR-safe</p>
        </form>
      </div>
    </div>
  )
}
